export const PROVINCE_CODES = [
  'AB',
  'BC',
  'MB',
  'NB',
  'NL',
  'NS',
  'NT',
  'NU',
  'ON',
  'PE',
  'QC',
  'SK',
  'YT',
] as const

export const PROVINCE_NAMES = [
  'Alberta',
  'British Columbia',
  'Manitoba',
  'New Brunswick',
  'Newfoundland',
  'Northwest Territories',
  'Nova Scotia',
  'Nunavut',
  'Ontario',
  'Prince Edward Island',
  'Quebec',
  'Saskatchewan',
  'Yukon',
] as const

export type ProvinceCode = (typeof PROVINCE_CODES)[number]
export type ProvinceName = (typeof PROVINCE_NAMES)[number]

export const PROVINCE_CODE_TO_NAME: Record<ProvinceCode, ProvinceName> = {
  AB: 'Alberta',
  BC: 'British Columbia',
  MB: 'Manitoba',
  NB: 'New Brunswick',
  NL: 'Newfoundland',
  NS: 'Nova Scotia',
  NT: 'Northwest Territories',
  NU: 'Nunavut',
  ON: 'Ontario',
  PE: 'Prince Edward Island',
  QC: 'Quebec',
  SK: 'Saskatchewan',
  YT: 'Yukon',
}

export const PROVINCE_NAME_TO_CODE: Record<ProvinceName, ProvinceCode> = {
  Alberta: 'AB',
  'British Columbia': 'BC',
  Manitoba: 'MB',
  'New Brunswick': 'NB',
  Newfoundland: 'NL',
  'Northwest Territories': 'NT',
  'Nova Scotia': 'NS',
  Nunavut: 'NU',
  Ontario: 'ON',
  'Prince Edward Island': 'PE',
  Quebec: 'QC',
  Saskatchewan: 'SK',
  Yukon: 'YT',
}

export const getProvinceCode = (name: ProvinceName): ProvinceCode => {
  return PROVINCE_NAME_TO_CODE[name]
}

export const getProvinceName = (code: ProvinceCode): ProvinceName => {
  return PROVINCE_CODE_TO_NAME[code]
}

/** Narrows a raw value to a known province code. */
export const isProvinceCode = (value: string | null | undefined): value is ProvinceCode => {
  return value != null && PROVINCE_CODE_TO_NAME[value as ProvinceCode] !== undefined
}

/**
 * SAP's Customer Group 1 (`VBAK-KVGR1`) is the province an order ships to, as a numeric code. Only
 * the codes BAT has documented (161–174) are mapped; 165 and 166 are both Newfoundland in SAP.
 */
export const CUSTOMER_GROUP_1_TO_PROVINCE: Record<string, ProvinceCode> = {
  '161': 'AB',
  '162': 'BC',
  '163': 'MB',
  '164': 'NB',
  '165': 'NL',
  '166': 'NL',
  '167': 'NS',
  '168': 'NT',
  '169': 'NU',
  '170': 'ON',
  '171': 'PE',
  '172': 'QC',
  '173': 'SK',
  '174': 'YT',
}

/**
 * The province an order's Customer Group 1 stands for, or the raw code when it is not one BAT has
 * documented — shown as-is so an unmapped order still says what SAP sent rather than going blank.
 * Callers that need a real province (the cutoff lookup) narrow the result with `isProvinceCode`.
 */
export const getProvinceFromCustomerGroup1 = (
  customerGroup1: string | null | undefined,
): string | null => {
  const code = customerGroup1?.trim()
  if (!code) return null
  return CUSTOMER_GROUP_1_TO_PROVINCE[code] ?? code
}

/** The provinces some Customer Group 1 code maps to, in `PROVINCE_CODES` order. */
export const CUSTOMER_GROUP_1_PROVINCES: ProvinceCode[] = PROVINCE_CODES.filter((code) =>
  Object.values(CUSTOMER_GROUP_1_TO_PROVINCE).includes(code),
)

/**
 * The inverse of `getProvinceFromCustomerGroup1`, for filtering: the Customer Group 1 codes a
 * displayed province value stands for. A value that is not a mapped province is taken to be a raw
 * code, the way the cockpit displays one.
 */
export const getCustomerGroup1Codes = (province: string): string[] => {
  const codes = Object.entries(CUSTOMER_GROUP_1_TO_PROVINCE)
    .filter(([, mapped]) => mapped === province)
    .map(([code]) => code)
  return codes.length > 0 ? codes : [province]
}

export const UNMARKED_PROVINCES_SUFFIX = 'UM'
